import { useEffect, useState, useCallback } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Bold, Italic, Strikethrough, List, ListOrdered, Heading1, Heading2,
  Quote, Undo, Redo, Link as LinkIcon, Image as ImageIcon, Code, Unlink,
} from "lucide-react";

type Props = {
  content: string;
  onChange: (html: string) => void;
};

type UrlDialogState = {
  open: boolean;
  type: "image" | "link";
  url: string;
};

export default function RichTextEditor({ content, onChange }: Props) {
  const [urlDialog, setUrlDialog] = useState<UrlDialogState>({
    open: false,
    type: "image",
    url: "",
  });

  const editor = useEditor({
    extensions: [
      StarterKit,
      Image,
      Link.configure({ openOnClick: false }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Sync content when the prop changes externally (e.g. after loading from DB)
  useEffect(() => {
    if (!editor) return;
    const currentContent = editor.getHTML();
    if (content !== currentContent) {
      editor.commands.setContent(content, false);
    }
  }, [content, editor]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      editor?.destroy();
    };
  }, [editor]);

  const openImageDialog = useCallback(() => {
    const existingSrc = editor?.getAttributes("image")?.src || "";
    setUrlDialog({ open: true, type: "image", url: existingSrc });
  }, [editor]);

  const openLinkDialog = useCallback(() => {
    const existingHref = editor?.getAttributes("link")?.href || "";
    setUrlDialog({ open: true, type: "link", url: existingHref });
  }, [editor]);

  const handleUrlSubmit = useCallback(() => {
    if (!editor || !urlDialog.url.trim()) {
      setUrlDialog((s) => ({ ...s, open: false, url: "" }));
      return;
    }

    if (urlDialog.type === "image") {
      editor.chain().focus().setImage({ src: urlDialog.url.trim() }).run();
    } else {
      editor.chain().focus().setLink({ href: urlDialog.url.trim() }).run();
    }

    setUrlDialog({ open: false, type: "image", url: "" });
  }, [editor, urlDialog]);

  const removeLink = useCallback(() => {
    editor?.chain().focus().unsetLink().run();
  }, [editor]);

  if (!editor) return null;

  const ToolBtn = ({ onClick, active, title, children }: { onClick: () => void; active?: boolean; title?: string; children: React.ReactNode }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`p-1.5 rounded transition-colors ${active ? "bg-primary text-white" : "text-gray-500 hover:bg-gray-100"}`}
    >
      {children}
    </button>
  );

  return (
    <>
      <div className="border rounded-xl overflow-hidden bg-white">
        <div className="flex flex-wrap gap-0.5 p-2 border-b bg-gray-50">
          <ToolBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold">
            <Bold className="w-4 h-4" />
          </ToolBtn>
          <ToolBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic">
            <Italic className="w-4 h-4" />
          </ToolBtn>
          <ToolBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Strikethrough">
            <Strikethrough className="w-4 h-4" />
          </ToolBtn>
          <ToolBtn onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive("code")} title="Code">
            <Code className="w-4 h-4" />
          </ToolBtn>
          <div className="w-px bg-gray-200 mx-1" />
          <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive("heading", { level: 1 })} title="Heading 1">
            <Heading1 className="w-4 h-4" />
          </ToolBtn>
          <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Heading 2">
            <Heading2 className="w-4 h-4" />
          </ToolBtn>
          <div className="w-px bg-gray-200 mx-1" />
          <ToolBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bullet List">
            <List className="w-4 h-4" />
          </ToolBtn>
          <ToolBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Ordered List">
            <ListOrdered className="w-4 h-4" />
          </ToolBtn>
          <ToolBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Blockquote">
            <Quote className="w-4 h-4" />
          </ToolBtn>
          <div className="w-px bg-gray-200 mx-1" />
          <ToolBtn onClick={openLinkDialog} active={editor.isActive("link")} title="Add Link">
            <LinkIcon className="w-4 h-4" />
          </ToolBtn>
          {editor.isActive("link") && (
            <ToolBtn onClick={removeLink} title="Remove Link">
              <Unlink className="w-4 h-4" />
            </ToolBtn>
          )}
          <ToolBtn onClick={openImageDialog} title="Add Image">
            <ImageIcon className="w-4 h-4" />
          </ToolBtn>
          <div className="w-px bg-gray-200 mx-1" />
          <ToolBtn onClick={() => editor.chain().focus().undo().run()} title="Undo">
            <Undo className="w-4 h-4" />
          </ToolBtn>
          <ToolBtn onClick={() => editor.chain().focus().redo().run()} title="Redo">
            <Redo className="w-4 h-4" />
          </ToolBtn>
        </div>
        <EditorContent
          editor={editor}
          className="prose prose-sm max-w-none p-4 min-h-[300px] focus:outline-none [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[280px]"
        />
      </div>

      {/* URL Dialog for Image / Link */}
      <Dialog open={urlDialog.open} onOpenChange={(open) => { if (!open) setUrlDialog((s) => ({ ...s, open: false, url: "" })); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {urlDialog.type === "image" ? "Insert Image" : "Insert Link"}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              {urlDialog.type === "image" ? "Image URL" : "Link URL"}
            </label>
            <input
              type="url"
              value={urlDialog.url}
              onChange={(e) => setUrlDialog((s) => ({ ...s, url: e.target.value }))}
              onKeyDown={(e) => { if (e.key === "Enter") handleUrlSubmit(); }}
              placeholder={urlDialog.type === "image" ? "https://example.com/image.jpg" : "https://example.com"}
              autoFocus
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUrlDialog((s) => ({ ...s, open: false, url: "" }))}>
              Cancel
            </Button>
            <Button onClick={handleUrlSubmit} disabled={!urlDialog.url.trim()}>
              {urlDialog.type === "image" ? "Insert Image" : "Insert Link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
